import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import { BatchDisplay, NewBatchInputs, UpdateBatchDataInputs, UserDisplay } from "@/types/admin";
import {
  AffiliationCourseBatch,
  AffiliationStatistic,
  BatchInfo,
  SubjectiveAnswerDisplay,
} from "@/types/affiliation";
import { ApiResponse, AzureUploadUrl, IdNamePair, PaginatedResponse } from "@/types/global";
import { StudentRollNumber } from "@/types/studentDashboard";

export default class AffiliationBatchApi extends ApiVerified {
  async getCourseBatchTreeForAffiliation(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationCourseBatch>>(
        `affiliation-batch/get-course-batch-tree`,
        { params: { affiliationId: affiliationId, onlyActive: true, includeChildren: true } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getDetailedAffiliationBatches(
    affiliationId: number,
    includeChildren: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<BatchDisplay>>(
        `affiliation-batch/get-detailed-affiliation-batches`,
        { params: { affiliationId: affiliationId, includeChildren: includeChildren } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async addNewBatch(updateData: NewBatchInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<number>(
        `affiliation-batch/add-new-batch`,
        updateData,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateBatch(updateData: UpdateBatchDataInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `affiliation-batch/update-batch`,
        updateData,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateBatchStatus(batchId: number, isActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `affiliation-batch/update-batch-status`,
        { BatchId: batchId, IsActive: isActive },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // The old `onlyActive` flag is gone; the endpoint now takes `yearRange` and defaults it
  // server-side, so we send nothing and let that default stand. Rows are IdNamePair where Id is
  // the starting year (2015) and Name the span label ("2015-16").
  async getAcademicSessions(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `affiliation-batch/get-academic-sessions`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationUsersByRoles(
    affiliationId: number,
    roleIdsCsv: string,
    isActive: boolean,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<UserDisplay>>(
        `affiliation-batch/get-affiliation-users-by-roles`,
        {
          params: {
            affiliationId: affiliationId,
            roleIdsCsv: roleIdsCsv,
            isActive: isActive,
            pageNumber: pageNumber,
            pageSize: pageSize,
          },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationCourseForBatch(batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<BatchInfo>(
        `affiliation-batch/get-affiliation-course-for-batch`,
        { params: { batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationBatchStudents(
    batchId: number,
    isActive: boolean,
    pageNumber: number,
    pageSize: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PaginatedResponse<UserDisplay>>(
        `affiliation-batch/get-batch-students`,
        {
          params: {
            batchId: batchId,
            isActive: isActive,
            pageNumber: pageNumber,
            pageSize: pageSize,
          },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationBatches(affiliationId: number, onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `affiliation-batch/get-affiliation-batches`,
        { params: { affiliationId: affiliationId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationStatistics(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<AffiliationStatistic>>(
        `affiliation-batch/get-affiliation-statistics`,
        { params: { affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentRollNumbers(batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<StudentRollNumber>>(
        `affiliation-batch/get-student-roll-numbers`,
        { params: { batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveStudentRollNumbers(studentRollNumbers: Array<StudentRollNumber>): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `affiliation-batch/save-student-roll-numbers`,
        studentRollNumbers,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // courseSubjectId filters on the Course↔Subject mapping id (NOT the master SubjectId). Empty
  // results come back as 404 "No subjective evaluations found.", not 200 [].
  async getSubjectiveEvaluationsForBatch(
    batchId: number,
    courseSubjectId: number | null,
    activityId: number,
    evaluationStatusId: number | null,
  ) {
    try {
      const response = await this.axiosInstance.get<Array<SubjectiveAnswerDisplay>>(
        `affiliation-batch/get-subjective-evaluations-for-batch`,
        {
          params: {
            batchId: batchId,
            courseSubjectId: courseSubjectId,
            activityId: activityId,
            evaluationStatusId: evaluationStatusId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // SAS target for the teacher's annotated answer sheet. update-subjective-evaluations stores
  // EvaluatedAnswer as a blob path — posting an inline `data:image/...` payload there fails — so
  // the canvas export is uploaded here first and only the returned url goes into the PUT.
  async getSubjectiveEvaluationsUploadUrl(
    activityId: number,
    activityInstanceId: number,
    questionId: number,
    contentType: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AzureUploadUrl>(
        `affiliation-batch/get-subjective-evaluations-upload-url`,
        {
          params: {
            activityId: activityId,
            activityInstanceId: activityInstanceId,
            questionId: questionId,
            contentType: contentType,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateSubjectiveEvaluationsForBatch(
    updatedSubjectiveAnswer: Array<SubjectiveAnswerDisplay>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `affiliation-batch/update-subjective-evaluations`,
        updatedSubjectiveAnswer,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // courseSubjectId filters on the Course↔Subject mapping id (NOT the master SubjectId). Empty
  // results come back as 404 "No subjective evaluations found.", not 200 [].
  async getSubjectiveEvaluationsForUser(
    userId: number,
    courseSubjectId: number | null,
    activityId: number,
    evaluationStatusId: number | null,
  ) {
    try {
      const response = await this.axiosInstance.get<Array<SubjectiveAnswerDisplay>>(
        `affiliation-batch/get-subjective-evaluations-for-user`,
        {
          params: {
            userId: userId,
            courseSubjectId: courseSubjectId,
            activityId: activityId,
            evaluationStatusId: evaluationStatusId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCourseChaptersForBatch(
    batchId: number,
    subjectId: number,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `affiliation-batch/get-subject-chapters-for-batch`,
        { params: { batchId: batchId, subjectId: subjectId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCommonChaptersForBatches(batchIdsCsv: string, subjectId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `affiliation-batch/get-common-chapters`,
        { params: { batchIdsCsv: batchIdsCsv, subjectId: subjectId, onlyActive: true } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getSubjectsForBatch(batchId: number, onlyActive: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `affiliation-batch/get-subjects-for-batch`,
        { params: { batchId: batchId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCommonSubjectsForBatches(batchIdsCsv: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `affiliation-batch/get-common-subjects`,
        { params: { batchIdsCsv: batchIdsCsv, onlyActive: true } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCoursesForAffiliation(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `affiliation-batch/get-affiliation-courses`,
        { params: { affiliationId: affiliationId, onlyActive: true } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getLiveClassFacultyDetails(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `affiliation-batch/get-faculty-for-affiliation`,
        { params: { affiliationId: affiliationId, onlyActive: true } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getModeratorsForAffiliation(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `affiliation-batch/get-moderators-for-affiliation`,
        { params: { affiliationId: affiliationId, onlyActive: true } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async checkZoomAccess(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<boolean>(
        `affiliation-batch/check-zoom-access`,
        { params: { affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
